// src/components/plan/PlanCardPage.jsx
// Pooled inbox SP5 — /plan/card, Profile's "Add New Card" (spec § Screens per
// app → Web: "a small dark card page on /plan/card using the same Stripe
// widget and POST /billing/card"; replaces the legacy CardElement path that
// sent a payment method from app code). POST /billing/card/setup-intent
// ($0) → Stripe confirms → POST /billing/card makes it the plan's card and
// clears a recorded failed payment. Only for a Stripe-billed plan: the server
// answers anyone else 409 not_stripe, and the page returns to Profile.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../ui";
import Loader from "../Loader";
import {
  GENERIC_ERROR,
  LOAD_FAILED,
  TRY_AGAIN,
} from "../onboarding/onboardingCopy";
import {
  createCardSetupIntent,
  saveCard,
} from "../../shared/_services/billing.services";
import PlanLayout from "./PlanLayout";
import { StripeCardForm } from "./StripeCardForm";
import { refreshStoredUser } from "./planNav";
import {
  BACK_TO_PROFILE,
  CARD_DECLINED,
  CARD_DETAILS,
  CARD_SUB,
  CARD_TITLE,
  PLEASE_WAIT,
  PROCESSED_BY_STRIPE,
  SAVE_CARD,
  STRIPE_UNREACHABLE,
} from "./planCopy";

const PROFILE_PATH = "/profile";
const UNREACHABLE = ["api_connection_error", "not_ready"];

export default function PlanCardPage() {
  const navigate = useNavigate();
  const h1 = useRef(null);
  const [load, setLoad] = useState({ status: "loading", data: null });
  const [busy, setBusy] = useState(false);
  const [cardError, setCardError] = useState(null);
  const alive = useRef(false);
  // The SetupIntent Stripe already confirmed: a retry after a failed save
  // reuses it (confirming a succeeded intent again is refused).
  const confirmedSi = useRef(null);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const read = useCallback(async () => {
    setLoad({ status: "loading", data: null });
    try {
      const res = await createCardSetupIntent();
      if (!alive.current) return;
      confirmedSi.current = null;
      setLoad({ status: "ready", data: res.data.clientSecret });
    } catch (err) {
      if (!alive.current) return;
      const code = err?.response?.data?.code;
      if (code === "not_stripe") {
        navigate(PROFILE_PATH, { replace: true });
        return;
      }
      setLoad({
        status: "error",
        data: code === "stripe_unavailable" ? STRIPE_UNREACHABLE : LOAD_FAILED,
      });
    }
  }, [navigate]);
  useEffect(() => {
    read();
  }, [read]);
  useEffect(() => {
    if (load.status === "ready") h1.current?.focus();
  }, [load.status]);

  const submit = async (confirm) => {
    setBusy(true);
    setCardError(null);
    let setupIntentId = confirmedSi.current;
    if (!setupIntentId) {
      const result = await confirm("setup");
      if (!alive.current) return;
      if (!result.ok) {
        setCardError(
          UNREACHABLE.includes(result.error.type)
            ? STRIPE_UNREACHABLE
            : result.error.message || CARD_DECLINED,
        );
        setBusy(false);
        return;
      }
      setupIntentId = result.intent.id;
      confirmedSi.current = setupIntentId;
    }
    try {
      await saveCard(setupIntentId);
      await refreshStoredUser();
      if (!alive.current) return;
      navigate(PROFILE_PATH);
    } catch (err) {
      if (!alive.current) return;
      const code = err?.response?.data?.code;
      setCardError(
        !err?.response || code === "stripe_unavailable"
          ? STRIPE_UNREACHABLE
          : err.response.data?.message || GENERIC_ERROR,
      );
      setBusy(false);
    }
  };

  if (load.status === "loading")
    return (
      <PlanLayout>
        <Loader />
      </PlanLayout>
    );
  if (load.status === "error")
    return (
      <PlanLayout>
        <div
          role="alert"
          className="tw-grid tw-justify-items-center tw-gap-4 tw-text-center"
        >
          <p className="tw-m-0">{load.data}</p>
          <Button variant="quiet" onClick={read}>
            {TRY_AGAIN}
          </Button>
        </div>
      </PlanLayout>
    );

  return (
    <PlanLayout>
      <div className="tw-mb-8 tw-grid tw-justify-items-center tw-gap-3 tw-text-center">
        <h1
          ref={h1}
          tabIndex={-1}
          className="tw-m-0 tw-text-2xl tw-font-bold focus:tw-outline-none"
        >
          {CARD_TITLE}
        </h1>
        <p className="tw-m-0 tw-text-md tw-text-text-muted">{CARD_SUB}</p>
      </div>
      <StripeCardForm clientSecret={load.data}>
        {({ field, ready, loadError, complete, confirm }) => (
          <form
            noValidate
            className="tw-grid tw-gap-6"
            onSubmit={(e) => {
              e.preventDefault();
              if (ready && complete && !busy) submit(confirm);
            }}
          >
            <section className="tw-rounded-lg tw-border tw-border-solid tw-border-divider tw-bg-surface tw-p-6">
              <fieldset className="tw-m-0 tw-min-w-0 tw-border-0 tw-p-0">
                <legend className="tw-mb-4 tw-p-0 tw-text-lg tw-font-bold">
                  {CARD_DETAILS}
                </legend>
                {field}
                {(cardError || loadError) && (
                  <p
                    role="alert"
                    className="tw-mb-0 tw-mt-3 tw-text-sm tw-text-danger"
                  >
                    {cardError || loadError}
                  </p>
                )}
              </fieldset>
            </section>
            <div className="tw-grid tw-justify-items-center tw-gap-3 tw-text-center">
              <Button
                type="submit"
                className="tw-w-full"
                disabled={!ready || !complete}
                loading={busy}
              >
                {busy ? PLEASE_WAIT : SAVE_CARD}
              </Button>
              <p role="status" className="tw-sr-only">
                {busy ? PLEASE_WAIT : ""}
              </p>
              <p className="tw-m-0 tw-text-sm tw-text-text-muted">
                {PROCESSED_BY_STRIPE}
              </p>
              <Button as={Link} to={PROFILE_PATH} variant="text">
                {BACK_TO_PROFILE}
              </Button>
            </div>
          </form>
        )}
      </StripeCardForm>
    </PlanLayout>
  );
}
